import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { integrationService, IntegrationStatus } from '@/api/integrations/integrationService';
import { useAuth } from '@/contexts/AuthContext';

interface IntegrationContextType {
    integrations: IntegrationStatus | null;
    isLoading: boolean;
    error: string | null;
    refreshIntegrations: () => Promise<void>;
}

const IntegrationContext = createContext<IntegrationContextType | undefined>(undefined);

interface IntegrationProviderProps {
    children: ReactNode;
}

export const IntegrationProvider: React.FC<IntegrationProviderProps> = ({ children }) => {
    const { user } = useAuth();
    const [integrations, setIntegrations] = useState<IntegrationStatus | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const loadIntegrations = async () => {
        if (!user) {
            setIsLoading(false);
            return;
        }

        try {
            setIsLoading(true);
            setError(null);
            const status = await integrationService.getActiveIntegrations();
            setIntegrations(status);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to load integrations');
            console.error('Error loading integrations:', err);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        loadIntegrations();
    }, [user]);

    return (
        <IntegrationContext.Provider
            value={{ integrations, isLoading, error, refreshIntegrations: loadIntegrations }}
        >
            {children}
        </IntegrationContext.Provider>
    );
};

export const useIntegrationContext = (): IntegrationContextType => {
    const ctx = useContext(IntegrationContext);
    if (!ctx) {
        throw new Error('useIntegrationContext must be used within an IntegrationProvider');
    }
    return ctx;
};
